
import { useAuth } from '@/hooks/useAuth';
import { Loader2, Landmark, Wallet, TrendingUp, PiggyBank, Trash2, Plus, Search, Users as UsersIcon, Pencil, Coins, CreditCard, Gavel } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription, CardFooter } from '@/components/ui/card';
import { Transaction, BankAccount, FixedTermDeposit, JartidexInvestment, BankSettings, JartidexHistory } from '@/lib/types';
import { httpsCallable } from 'firebase/functions';
import { functions, db } from '@/firebase/config';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { collection, query, onSnapshot, orderBy } from 'firebase/firestore';
import { sanitizeFirestoreData } from '@/lib/utils';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { admin_updateJartidex, admin_updateBankSettings, getJartidexHistory, getBankSettings, admin_getAllInvestments, admin_getAllAccounts, admin_updateAccount, admin_deleteAccount, admin_createAccount, searchUsersByNickname, admin_migrateBalanceToOro, admin_issueFine } from '@/lib/actions';

const getAllTransactions = httpsCallable(functions, 'getAllTransactions');


function InvestmentChart({ data }: { data: JartidexHistory[] }) {
    const formattedData = useMemo(() => data.map(item => ({
        date: format(new Date(item.date), 'dd/MM'),
        value: item.value,
    })), [data]);

    return (
        <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={formattedData} margin={{ top: 5, right: 20, left: -20, bottom: 5 }}>
                <defs>
                    <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}/>
                    </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border) / 0.5)" />
                <XAxis dataKey="date" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }} />
                <YAxis tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }} domain={['dataMin - 100', 'dataMax + 100']} tickFormatter={(value) => `${value.toLocaleString('es-ES')}`} />
                <Tooltip
                    contentStyle={{ backgroundColor: 'hsl(var(--background))', border: '1px solid hsl(var(--border))' }}
                    labelStyle={{ color: 'hsl(var(--foreground))' }}
                    formatter={(value: number) => [`${value.toFixed(2)} Jartis`, "Valor"]}
                />
                <Area type="monotone" dataKey="value" stroke="hsl(var(--primary))" fillOpacity={1} fill="url(#colorValue)" />
            </AreaChart>
        </ResponsiveContainer>
    );
};

function SettingsManager({ settings, onUpdate }: { settings: BankSettings | null, onUpdate: () => void }) {
    const [rates, setRates] = useState(settings?.fixedDepositRates || []);
    const [penalty, setPenalty] = useState((settings?.cancellationPenalty || 0) * 100);
    const { toast } = useToast();
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    useEffect(() => {
        setRates(settings?.fixedDepositRates || []);
        setPenalty((settings?.cancellationPenalty || 0) * 100);
    }, [settings]);

    const handleRateChange = (index: number, field: 'days' | 'rate', value: string) => {
        const newRates = [...rates];
        const numValue = Number(value);
        if (field === 'rate') {
            newRates[index] = { ...newRates[index], [field]: numValue / 100 };
        } else {
            newRates[index] = { ...newRates[index], [field]: numValue };
        }
        setRates(newRates);
    };
    
    const addRate = () => setRates([...rates, { days: 30, rate: 0.05 }]);
    const removeRate = (index: number) => setRates(rates.filter((_, i) => i !== index));

    const handleSave = async () => {
        setIsSubmitting(true);
        const updates = {
            fixedDepositRates: rates.map((r: any) => ({ days: Number(r.days), rate: Number(r.rate) })),
            cancellationPenalty: Number(penalty) / 100
        };
        try {
            const result = await admin_updateBankSettings(updates);
            if (result.success) {
                toast({ title: "Éxito", description: "Configuración del banco actualizada." });
                onUpdate();
            } else {
                 toast({ title: "Error", description: result.message, variant: "destructive" });
            }
        } catch (e: any) {
            toast({ title: "Error", description: e.message, variant: "destructive" });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="space-y-6">
            <Card>
                <CardHeader>
                    <CardTitle>Tipos de Interés para Depósitos a Plazo Fijo</CardTitle>
                    <CardDescription>Define los plazos y rentabilidades que los usuarios pueden contratar.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    {rates.map((rate: any, index: number) => (
                        <div key={index} className="flex items-center gap-4">
                            <Input type="number" placeholder="Días" value={rate.days} onChange={e => handleRateChange(index, 'days', e.target.value)} />
                            <Input type="number" placeholder="Tasa (%)" value={rate.rate * 100} onChange={e => handleRateChange(index, 'rate', e.target.value)} />
                            <Button variant="destructive" size="icon" onClick={() => removeRate(index)}>
                                <Trash2 className="h-4 w-4"/>
                            </Button>
                        </div>
                    ))}
                    <Button variant="outline" onClick={addRate}><Plus className="mr-2"/>Añadir Opción</Button>
                </CardContent>
            </Card>
            <Card>
                <CardHeader>
                    <CardTitle>Penalización por Cancelación Anticipada</CardTitle>
                </CardHeader>
                <CardContent className="flex items-center gap-4">
                    <Input type="number" value={penalty} onChange={e => setPenalty(Number(e.target.value))} />
                    <span className="text-muted-foreground">% del capital</span>
                </CardContent>
            </Card>
            <div className="flex justify-end">
                <Button onClick={handleSave} disabled={isSubmitting}>
                    {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin"/>}
                    Guardar Configuración
                </Button>
            </div>
        </div>
    );
}


function JartidexControl({ onUpdate, history }: { onUpdate: () => void, history: JartidexHistory[] }) {
    const [change, setChange] = useState<number | string>('');
    const [description, setDescription] = useState('');
    const { toast } = useToast();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleUpdate = async () => {
        if(change === '') {
            toast({ title: "Error", description: "El campo de variación no puede estar vacío.", variant: "destructive" });
            return;
        }
        setIsSubmitting(true);
        try {
            const result = await admin_updateJartidex({ changePercent: Number(change) / 100, eventDescription: description });
            if (result.success) {
                toast({ title: "Éxito", description: "El Jartidex35 ha sido actualizado." });
                onUpdate();
                setChange('');
                setDescription('');
            } else {
                 toast({ title: "Error", description: result.message, variant: "destructive" });
            }
        } catch (e: any) {
            toast({ title: "Error", description: e.message, variant: "destructive" });
        } finally {
            setIsSubmitting(false);
        }
    };
    
    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
                <CardHeader>
                    <CardTitle>Control del Jartidex35</CardTitle>
                    <CardDescription>Introduce un evento de mercado para alterar el valor del índice.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div>
                        <Label htmlFor="jartidex-change">Variación Porcentual (%)</Label>
                        <Input id="jartidex-change" type="number" value={change} onChange={e => setChange(e.target.value)} placeholder="Ej: 5 para +5%, -3 para -3%" />
                    </div>
                    <div>
                        <Label htmlFor="jartidex-desc">Descripción del Evento (Opcional)</Label>
                        <Input id="jartidex-desc" value={description} onChange={e => setDescription(e.target.value)} placeholder="Ej: 'Éxito en la feria de Jartiland'" />
                    </div>
                </CardContent>
                <CardFooter>
                    <Button onClick={handleUpdate} disabled={isSubmitting}>
                        {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin"/>}
                        Aplicar Variación
                    </Button>
                </CardFooter>
            </Card>
             <Card>
                <CardHeader><CardTitle>Gráfico del Jartidex35</CardTitle></CardHeader>
                <CardContent>
                    {history.length > 0 ? <InvestmentChart data={history} /> : <p className="text-center text-muted-foreground py-10">No hay datos históricos.</p>}
                </CardContent>
            </Card>
        </div>
    )
}

// ── Gestión de cuentas bancarias ──
type AdminAccount = BankAccount & { ownerNickname?: string; sharedWithNames?: string[]; pin?: string };

function AccountEditor({ account, onClose, onChanged }: { account: AdminAccount; onClose: () => void; onChanged: () => void }) {
  const { toast } = useToast();
  const [form, setForm] = useState({
    accountName: account.accountName || '',
    accountType: account.accountType || 'secundaria',
    oro: String(account.oro ?? 0),
    plata: String(account.plata ?? 0),
    bronce: String(account.bronce ?? 0),
    pin: '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k: string, v: any) => setForm((p) => ({ ...p, [k]: v }));

  // ── Multa ──
  const [fine, setFine] = useState({ amount: '', currency: 'oro', reason: '' });
  const [fining, setFining] = useState(false);
  const setFineField = (k: string, v: any) => setFine((p) => ({ ...p, [k]: v }));

  const issueFine = async () => {
    const amt = Math.floor(Number(fine.amount));
    if (!amt || amt <= 0) { toast({ title: 'Importe inválido', description: 'Indica un importe mayor que 0.', variant: 'destructive' }); return; }
    if (!fine.reason.trim()) { toast({ title: 'Falta el motivo', description: 'Escribe el motivo de la multa.', variant: 'destructive' }); return; }
    setFining(true);
    const res = await admin_issueFine({ accountId: account.id, amount: amt, currency: fine.currency, reason: fine.reason.trim() });
    if (res.success) {
      toast({ title: 'Multa aplicada', description: res.message });
      setFine({ amount: '', currency: 'oro', reason: '' });
      onChanged();
    } else {
      toast({ title: 'Error', description: res.message, variant: 'destructive' });
    }
    setFining(false);
  };

  const save = async () => {
    setSaving(true);
    const updates: any = {
      accountName: form.accountName,
      accountType: form.accountType,
      oro: Number(form.oro),
      plata: Number(form.plata),
      bronce: Number(form.bronce),
    };
    if (form.pin.trim()) updates.pin = form.pin.trim();
    const res = await admin_updateAccount(account.id, updates);
    if (res.success) { toast({ title: 'Guardado', description: res.message }); onChanged(); }
    else toast({ title: 'Error', description: res.message, variant: 'destructive' });
    setSaving(false);
  };

  const del = async () => {
    const res = await admin_deleteAccount(account.id);
    if (res.success) { toast({ title: 'Eliminada', description: res.message }); onChanged(); onClose(); }
    else toast({ title: 'Error', description: res.message, variant: 'destructive' });
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Editar cuenta</DialogTitle>
          <DialogDescription>IBAN {account.iban} · Propietario: {account.ownerNickname || account.ownerId}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Nombre</Label><Input value={form.accountName} onChange={(e) => set('accountName', e.target.value)} /></div>
            <div className="space-y-1.5">
              <Label>Tipo</Label>
              <Select value={form.accountType} onValueChange={(v) => set('accountType', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="secundaria">Secundaria</SelectItem>
                  <SelectItem value="principal">Principal</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5"><Label className="text-amber-400">Oro</Label><Input type="number" value={form.oro} onChange={(e) => set('oro', e.target.value)} /></div>
            <div className="space-y-1.5"><Label className="text-slate-300">Plata</Label><Input type="number" value={form.plata} onChange={(e) => set('plata', e.target.value)} /></div>
            <div className="space-y-1.5"><Label className="text-amber-700">Bronce</Label><Input type="number" value={form.bronce} onChange={(e) => set('bronce', e.target.value)} /></div>
          </div>
          <div className="space-y-1.5">
            <Label>Cambiar PIN (6 dígitos, opcional)</Label>
            <Input value={form.pin} onChange={(e) => set('pin', e.target.value)} placeholder="Dejar vacío para no cambiarlo" maxLength={6} />
          </div>
          {account.sharedWithNames && account.sharedWithNames.length > 0 && (
            <p className="text-xs text-muted-foreground">Compartida con: {account.sharedWithNames.join(', ')}</p>
          )}

          {/* ── Poner multa ── */}
          <div className="space-y-3 rounded-2xl border border-rose-500/30 bg-rose-500/5 p-4">
            <p className="flex items-center gap-2 text-sm font-semibold text-rose-300"><Gavel className="h-4 w-4" /> Poner multa</p>
            <p className="text-xs text-muted-foreground">Se descuenta el importe automáticamente de esta cuenta y se avisa al propietario por el buzón. Si el saldo es menor, se cobra lo disponible.</p>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5"><Label>Importe</Label><Input type="number" min={1} value={fine.amount} onChange={(e) => setFineField('amount', e.target.value)} placeholder="0" /></div>
              <div className="space-y-1.5">
                <Label>Moneda</Label>
                <Select value={fine.currency} onValueChange={(v) => setFineField('currency', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="oro">Oro</SelectItem>
                    <SelectItem value="plata">Plata</SelectItem>
                    <SelectItem value="bronce">Bronce</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5"><Label>Motivo</Label><Input value={fine.reason} onChange={(e) => setFineField('reason', e.target.value)} placeholder="Ej: Infracción del reglamento cofrade" /></div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="w-full border-rose-500/40 text-rose-300" disabled={fining}>{fining ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Gavel className="mr-2 h-4 w-4" />} Aplicar multa</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>¿Aplicar multa a "{form.accountName}"?</AlertDialogTitle>
                  <AlertDialogDescription>Se retirarán <b>{Math.floor(Number(fine.amount)) || 0} {fine.currency}</b> de la cuenta y le llegará una carta de aviso al buzón del propietario. Esta acción afecta a un saldo real.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction onClick={issueFine} className="bg-rose-600 hover:bg-rose-700">Aplicar multa</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          <AlertDialog>
            <AlertDialogTrigger asChild><Button variant="ghost" className="text-destructive"><Trash2 className="mr-1 h-4 w-4" /> Eliminar</Button></AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>¿Eliminar "{form.accountName}"?</AlertDialogTitle>
                <AlertDialogDescription>Esta acción es permanente. Se eliminará la cuenta y su saldo.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={del} className="bg-destructive hover:bg-destructive/80">Eliminar</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancelar</Button>
            <Button onClick={save} disabled={saving}>{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Guardar</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateAccountDialog({ onCreated }: { onCreated: () => void }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [results, setResults] = useState<{ id: string; nickname: string }[]>([]);
  const [owner, setOwner] = useState<{ id: string; nickname: string } | null>(null);
  const [form, setForm] = useState({ accountName: '', pin: '', oro: '0', plata: '0', bronce: '0', accountType: 'secundaria' });
  const [creating, setCreating] = useState(false);
  const set = (k: string, v: any) => setForm((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    const q = term.trim();
    if (q.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => { const { users } = await searchUsersByNickname(q); setResults((users || []).map((u: any) => ({ id: u.id, nickname: u.nickname }))); }, 350);
    return () => clearTimeout(t);
  }, [term]);

  const create = async () => {
    if (!owner) { toast({ title: 'Elige un propietario', variant: 'destructive' }); return; }
    if (form.accountName.length < 3 || !/^\d{6}$/.test(form.pin)) { toast({ title: 'Datos inválidos', description: 'Nombre (≥3) y PIN de 6 dígitos.', variant: 'destructive' }); return; }
    setCreating(true);
    const res = await admin_createAccount({ ownerId: owner.id, accountName: form.accountName, pin: form.pin, oro: Number(form.oro), plata: Number(form.plata), bronce: Number(form.bronce), accountType: form.accountType });
    if (res.success) { toast({ title: 'Cuenta creada', description: res.message }); setOpen(false); setOwner(null); setTerm(''); setForm({ accountName: '', pin: '', oro: '0', plata: '0', bronce: '0', accountType: 'secundaria' }); onCreated(); }
    else toast({ title: 'Error', description: res.message, variant: 'destructive' });
    setCreating(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button className="gap-2" onClick={() => setOpen(true)}><Plus className="h-4 w-4" /> Crear cuenta</Button>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Nueva cuenta bancaria</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Propietario</Label>
            {owner ? (
              <div className="flex items-center justify-between rounded-lg border border-white/10 bg-muted/30 p-2">
                <span className="text-sm">{owner.nickname}</span>
                <Button size="sm" variant="ghost" onClick={() => setOwner(null)}>Cambiar</Button>
              </div>
            ) : (
              <>
                <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" /><Input value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Buscar usuario..." className="pl-9" /></div>
                {results.length > 0 && (
                  <div className="mt-1 space-y-1">
                    {results.map((u) => <button key={u.id} onClick={() => setOwner(u)} className="block w-full rounded-lg border border-white/8 bg-muted/20 p-2 text-left text-sm hover:bg-muted/40">{u.nickname}</button>)}
                  </div>
                )}
              </>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Nombre de la cuenta</Label><Input value={form.accountName} onChange={(e) => set('accountName', e.target.value)} /></div>
            <div className="space-y-1.5"><Label>PIN (6 dígitos)</Label><Input value={form.pin} onChange={(e) => set('pin', e.target.value)} maxLength={6} /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5"><Label className="text-amber-400">Oro</Label><Input type="number" value={form.oro} onChange={(e) => set('oro', e.target.value)} /></div>
            <div className="space-y-1.5"><Label className="text-slate-300">Plata</Label><Input type="number" value={form.plata} onChange={(e) => set('plata', e.target.value)} /></div>
            <div className="space-y-1.5"><Label className="text-amber-700">Bronce</Label><Input type="number" value={form.bronce} onChange={(e) => set('bronce', e.target.value)} /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
          <Button onClick={create} disabled={creating}>{creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Crear</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AccountsManager() {
  const { toast } = useToast();
  const [accounts, setAccounts] = useState<AdminAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<AdminAccount | null>(null);
  const [migrating, setMigrating] = useState(false);

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    const res = await admin_getAllAccounts();
    if (res.success) setAccounts(res.accounts);
    setLoading(false);
  }, []);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  const runMigration = async () => {
    setMigrating(true);
    const res = await admin_migrateBalanceToOro();
    if (res.success) toast({ title: 'Migración completada', description: `${res.migrated} cuenta(s) migradas (balance → oro), ${res.skipped} sin balance.` });
    else toast({ title: 'Error', description: (res as any).message || 'No se pudo migrar.', variant: 'destructive' });
    setMigrating(false);
    fetchAccounts();
  };

  // ¿Hay cuentas con saldo legacy 'balance' pendiente de migrar?
  const pendingBalance = accounts.filter((a: any) => typeof a.balance === 'number' && a.balance > 0).length;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = [...accounts].sort((a, b) => (a.accountName || '').localeCompare(b.accountName || ''));
    if (!q) return list;
    return list.filter((a) => `${a.accountName} ${a.iban} ${a.ownerNickname}`.toLowerCase().includes(q));
  }, [accounts, search]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por nombre, IBAN o propietario..." className="pl-9" />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">{accounts.length} cuentas</span>
          <CreateAccountDialog onCreated={fetchAccounts} />
        </div>
      </div>

      {/* Migración de saldos legacy balance → oro */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4">
        <div className="flex items-center gap-2 text-sm">
          <Coins className="h-5 w-5 text-amber-400" />
          <span>
            Migrar saldos antiguos: pasa el campo <b>balance</b> a <b>oro</b> en las cuentas que aún lo tengan.
            {pendingBalance > 0 ? <b className="text-amber-300"> ({pendingBalance} pendiente(s))</b> : <span className="text-muted-foreground"> (no se detectan pendientes)</span>}
          </span>
        </div>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" className="border-amber-500/40 text-amber-300" disabled={migrating}>{migrating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Migrar balance → oro</Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Migrar todos los saldos legacy?</AlertDialogTitle>
              <AlertDialogDescription>Para cada cuenta con <b>balance</b>, se hará <b>oro = oro + balance</b> y se borrará <b>balance</b>. Es idempotente (puedes ejecutarlo sin riesgo de duplicar). Afecta a saldos reales.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={runMigration}>Migrar ahora</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {filtered.map((a) => (
            <button key={a.id} onClick={() => setEditing(a)} className="group rounded-2xl border border-white/8 bg-card/40 p-4 text-left transition-colors hover:border-primary/40">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="flex items-center gap-2 font-semibold"><CreditCard className="h-4 w-4 text-primary" /> {a.accountName}</p>
                  <p className="truncate text-xs text-muted-foreground">{a.iban}</p>
                </div>
                <Badge className={a.accountType === 'principal' ? 'bg-amber-500/20 text-amber-400' : 'bg-sky-500/20 text-sky-400'}>{a.accountType || 'secundaria'}</Badge>
              </div>
              <p className="mt-2 flex items-center gap-1 text-xs text-muted-foreground"><UsersIcon className="h-3 w-3" /> {a.ownerNickname || a.ownerId}</p>
              <div className="mt-3 flex gap-3 text-sm">
                <span className="flex items-center gap-1 font-bold text-amber-400"><Coins className="h-3.5 w-3.5" />{(a.oro ?? 0).toLocaleString('es-ES')}</span>
                <span className="font-bold text-slate-300">{(a.plata ?? 0).toLocaleString('es-ES')} P</span>
                <span className="font-bold text-amber-700">{(a.bronce ?? 0).toLocaleString('es-ES')} B</span>
                <Pencil className="ml-auto h-4 w-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
              </div>
            </button>
          ))}
        </div>
      )}

      {editing && <AccountEditor account={editing} onClose={() => setEditing(null)} onChanged={fetchAccounts} />}
    </div>
  );
}

export default function AdminBancoPage() {
  const { user, userData, loading } = useAuth();
  const navigate = useNavigate();
  const [isLoadingPage, setIsLoadingPage] = useState(true);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [allDeposits, setAllDeposits] = useState<FixedTermDeposit[]>([]);
  const [allInvestments, setAllInvestments] = useState<JartidexInvestment[]>([]);
  const [settings, setSettings] = useState<BankSettings | null>(null);
  const [jartidexHistory, setJartidexHistory] = useState<JartidexHistory[]>([]);

  const fetchData = useCallback(async () => {
    setIsLoadingPage(true);
    try {
      const [txRes, settingsRes, historyRes, allInvRes] = await Promise.all([
        getAllTransactions(),
        getBankSettings(),
        getJartidexHistory(),
        admin_getAllInvestments() 
      ]);
      
      const txData = txRes.data as { success: boolean, transactions?: Transaction[] };
      if (txData.success && txData.transactions) setTransactions(txData.transactions);
      
      if (settingsRes.settings) setSettings(settingsRes.settings);
      
      if (historyRes.history) setJartidexHistory(historyRes.history);
      
      if (allInvRes.success) {
        setAllDeposits(allInvRes.deposits);
        setAllInvestments(allInvRes.investments);
      }

    } catch (e) {
      console.error("Failed to load admin bank data", e);
    } finally {
      setIsLoadingPage(false);
    }
  }, []);

  useEffect(() => {
    if (!loading && userData?.role === 'admin') {
      fetchData();
    } else if (!loading) {
      navigate('/jartinet', { replace: true });
    }
  }, [user, userData, loading, navigate, fetchData]);
  

  if (loading || isLoadingPage) {
    return (
      <div className="flex h-[calc(100vh-10rem)] w-full items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
        <p className="ml-4">Cargando gestión del banco...</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-8">
       <div className="space-y-6">
        <div className="text-center max-w-3xl mx-auto">
          <h1 className="font-headline text-5xl font-bold text-primary flex items-center justify-center gap-4">
            <Landmark className="h-12 w-12" />
            Gestión del Banco
          </h1>
          <p className="mt-4 text-lg text-muted-foreground">
            Administra y supervisa las transacciones y productos de inversión de JartiBank.
          </p>
        </div>
      </div>
      
      <Tabs defaultValue="accounts">
          <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="accounts">Cuentas</TabsTrigger>
              <TabsTrigger value="transactions">Transferencias</TabsTrigger>
              <TabsTrigger value="deposits">Depósitos</TabsTrigger>
              <TabsTrigger value="jartidex">Jartidex</TabsTrigger>
              <TabsTrigger value="settings">Configuración</TabsTrigger>
          </TabsList>

          <TabsContent value="accounts" className="mt-6">
            <AccountsManager />
          </TabsContent>

          <TabsContent value="transactions" className="mt-6">
            <Card>
                <CardHeader>
                    <CardTitle>Historial de Transferencias</CardTitle>
                    <CardDescription>Lista de todas las transferencias realizadas en el servidor.</CardDescription>
                </CardHeader>
                <CardContent>
                    {transactions.length > 0 ? (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Fecha</TableHead>
                            <TableHead>Desde</TableHead>
                            <TableHead>Hacia</TableHead>
                            <TableHead>Concepto</TableHead>
                            <TableHead className="text-right">Cantidad</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {transactions.map(tx => (
                             <TableRow key={tx.id}>
                               <TableCell>{tx.date ? format(new Date(tx.date), "dd/MM/yyyy HH:mm", { locale: es }) : 'N/A'}</TableCell>
                               <TableCell>{tx.fromAccountName || tx.fromIban}</TableCell>
                               <TableCell>{tx.toAccountName || tx.toIban}</TableCell>
                               <TableCell>{tx.concept}</TableCell>
                               <TableCell className="text-right font-medium">{tx.amount.toFixed(2)} Jartis</TableCell>
                             </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <div className="text-center py-12"><Wallet className="mx-auto h-12 w-12 text-muted-foreground" /><h3 className="mt-4 text-lg font-semibold">No hay transacciones</h3><p className="mt-1 text-sm text-muted-foreground">Aún no se ha realizado ninguna transferencia.</p></div>
                    )}
                </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="deposits" className="mt-6">
              <Card>
                  <CardHeader><CardTitle>Depósitos Activos</CardTitle></CardHeader>
                  <CardContent>{allDeposits.length > 0 ? (
                         <Table>
                             <TableHeader>
                                 <TableRow>
                                     <TableHead>Usuario ID</TableHead>
                                     <TableHead>Cantidad</TableHead>
                                     <TableHead>Plazo</TableHead>
                                     <TableHead>Estado</TableHead>
                                     <TableHead>Fecha Fin</TableHead>
                                 </TableRow>
                             </TableHeader>
                             <TableBody>
                                 {allDeposits.map(d => (
                                     <TableRow key={d.id}>
                                         <TableCell className="truncate max-w-xs">{d.userId}</TableCell>
                                         <TableCell>{d.amount.toFixed(2)}</TableCell>
                                         <TableCell>{d.termDays} días</TableCell>
                                         <TableCell>{d.status}</TableCell>
                                         <TableCell>{format(new Date(d.endDate), 'dd/MM/yyyy')}</TableCell>
                                     </TableRow>
                                 ))}
                             </TableBody>
                         </Table>
                      ) : <p className="text-muted-foreground text-center py-8">No hay depósitos activos.</p>}
                  </CardContent>
              </Card>
          </TabsContent>

          <TabsContent value="jartidex" className="mt-6 space-y-6">
              <JartidexControl onUpdate={fetchData} history={jartidexHistory} />
              <Card>
                  <CardHeader><CardTitle>Inversiones en Jartidex35</CardTitle></CardHeader>
                  <CardContent>{allInvestments.length > 0 ? (
                           <Table>
                             <TableHeader>
                                 <TableRow>
                                     <TableHead>Usuario ID</TableHead>
                                     <TableHead>Participaciones</TableHead>
                                     <TableHead>Coste Total</TableHead>
                                     <TableHead>Fecha Compra</TableHead>
                                     <TableHead>Estado</TableHead>
                                 </TableRow>
                             </TableHeader>
                             <TableBody>
                                 {allInvestments.map(i => (
                                     <TableRow key={i.id}>
                                         <TableCell className="truncate max-w-xs">{i.userId}</TableCell>
                                         <TableCell>{i.shares.toFixed(4)}</TableCell>
                                         <TableCell>{i.totalCost.toFixed(2)} Jartis</TableCell>
                                         <TableCell>{format(new Date(i.purchaseDate), 'dd/MM/yyyy')}</TableCell>
                                         <TableCell>{i.status}</TableCell>
                                     </TableRow>
                                 ))}
                             </TableBody>
                         </Table>
                      ) : <p className="text-muted-foreground text-center py-8">No hay inversiones activas en Jartidex35.</p>}
                  </CardContent>
              </Card>
          </TabsContent>
          
          <TabsContent value="settings" className="mt-6">
             <SettingsManager settings={settings} onUpdate={fetchData} />
          </TabsContent>
      </Tabs>
    </div>
  );
}
